import {BridgeNode} from './instance/BridgeNode.ts';
import {Config} from './config/Config.ts';
import {BOOLEAN, NUMBER, type PropertyType, type PropertyTypeConverter, STRING} from './config/Properties.ts';

import info from '../package.json' with {type: 'json'};

import {readFileSync} from 'node:fs';
import {format} from 'node:util';

let hadFatalError = false;
const reportedErrors = new Set();
function getErrorMessage(error: unknown) {
    if (typeof error !== 'object' || error === null) {
        return String(error);
    }
    if (typeof error['stack'] === 'string') {
        return error['stack'];
    }
    return format('%o', error);
}

function onFatalError(error) {

    process.exitCode = 2;
    hadFatalError = true;

    const errorMessage = `
io.Bridge: ${info.version}

${getErrorMessage(error)}`;

    if (!reportedErrors.has(errorMessage)) {
        console.error(error);
        reportedErrors.add(errorMessage);
    }
}

function propertyNameToEnvKey(name: string): string {
    return `IO_BRIDGE_${name.toUpperCase().replaceAll('-', '').replaceAll('.', '_')}`;
}

function loadConfig<T extends PropertyType=string>(name: string, converter: PropertyTypeConverter<T>): T | undefined {
    const cmdArg = process.argv.find(arg => {return arg.startsWith(`--${name}=`);})?.split('=')[1];
    if (cmdArg) {
        return converter.convert(cmdArg);
    }
    const envKey = propertyNameToEnvKey(name);
    const envVar = process.env[envKey];
    if (envVar) {
        return converter.convert(envVar);
    }
}

function loadLicense() {
    const config = loadConfig('license.key', STRING);
    if (config !== undefined) {
        return config;
    }
    const file = loadConfig('license.key.file', STRING);
    if (file !== undefined) {
        return readFileSync(file, {encoding: 'ascii'}).trim();
    }
}

try {
    process.on('uncaughtException', onFatalError);
    process.on('unhandledRejection', onFatalError);

    await import('dotenv/config');

    const config = new Config();
    config.license ??= loadLicense();
    config.server.port ??= loadConfig('server.port', NUMBER) ?? 8383;
    config.server.host ??= loadConfig('server.host', STRING);
    config.server.wsPingInterval ??= loadConfig('server.ws-ping-interval', NUMBER);
    config.gateway.enabled ??= loadConfig('gateway.enabled', BOOLEAN);
    config.gateway.contexts.lifetime ??= loadConfig('gateway.contexts.lifetime', STRING) as 'retained' | 'ref-counted' | 'ownership';
    config.server.auth.type = (loadConfig('server.auth.type', STRING) ?? 'none') as 'none' | 'basic' | 'oauth2';
    config.server.cors.allowOrigin = loadConfig('server.cors.allow-origin', STRING) ?? '*';
    config.server.cors.allowCredentials = loadConfig('server.cors.allow-credentials', BOOLEAN) ?? true;
    config.server.cors.disabled = (loadConfig('server.cors.disabled', BOOLEAN) ?? false) ? true: undefined;
    const bridge = new BridgeNode(config);

    await bridge.start();

}
catch (error) {
    onFatalError(error);
}
